import React from 'react';
import { Button, Col, FormGroup, Input, Label, Row } from 'reactstrap';
import { IconButton } from '@mui/material';
import { Add, Delete } from '@material-ui/icons';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import currencyFormatter from '../../../utils/currencyFormatter';
import { notifyWarning } from '../../../utils/notify';
import FormInputTooltip from '../../Template/Tooltip/FormInputTooltip';
import {
  ENTREGAVEL_AGENDAMENTO_DATA_FIXA,
  ENTREGAVEL_AGENDAMENTO_PRAZO,
  applyEntregavelAgendamentoMode,
  buildEntregaveisEmLote,
  buildEntregavelParcela,
  buildEntregavelParcelaFromDraft,
  calcularPctEntregavel,
  calcularValorEntregavel,
  entregaveisTotalBateComValorGlobal,
  getEntregavelAgendamentoMode,
  getSomaPercentualEntregaveis,
  getSomaValorEntregaveis,
  getValorPctFromEntregavel,
  isEntregavelDraftComValor,
  isEntregavelDraftVazio,
  renumberEntregaveis,
  validarEntregavelDraft,
} from './contrFornEntregaveisUtils';

const EMPTY_DRAFT = {
  descricao: '',
  prazoDias: '',
  dtPrev: null,
  dtPrevRemembered: null,
  prazoRemembered: null,
  valorPct: '',
  valorVlr: '',
  valueSource: 'pct',
  agendamentoMode: ENTREGAVEL_AGENDAMENTO_PRAZO,
};

const fieldLabelStyle = {
  fontSize: '12px',
  marginBottom: '2px',
};

const sectionDividerStyle = {
  borderTop: '1px solid #e6e6e6',
  margin: '0 0rem 1.25rem',
};

const PRAZO_ENTREGA_TOOLTIP = 'Prazo em dias para entregar o entregável depois da data de assinatura do contrato.';
const DATA_FIXA_TOOLTIP = 'Data prevista já definida. Não será recalculada na assinatura do contrato.';

const PrazoEntregaLabel = () => (
  <div className="d-flex align-items-center">
    <Label className="text-muted mb-0" style={fieldLabelStyle}>Prazo em dias p/ entrega</Label>
    <FormInputTooltip text={PRAZO_ENTREGA_TOOLTIP} />
  </div>
);

const DataPrevistaLabel = () => (
  <div className="d-flex align-items-center">
    <Label className="text-muted mb-0" style={fieldLabelStyle}>Data prevista</Label>
    <FormInputTooltip text={DATA_FIXA_TOOLTIP} />
  </div>
);

const preventWheelChange = (event) => {
  event.target.blur();
};

function AgendamentoModeToggle({
  idPrefix,
  mode,
  disabled,
  onChange,
}) {
  return (
    <FormGroup className="mb-2">
      <Label className="text-muted d-block" style={fieldLabelStyle}>Agendamento</Label>
      <div className="d-flex flex-wrap" style={{ gap: '0.75rem' }}>
        <FormGroup check inline className="mb-0">
          <Input
            type="radio"
            id={`${idPrefix}-prazo`}
            name={`${idPrefix}-agendamento`}
            checked={mode === ENTREGAVEL_AGENDAMENTO_PRAZO}
            disabled={disabled}
            onChange={() => onChange(ENTREGAVEL_AGENDAMENTO_PRAZO)}
          />
          <Label check for={`${idPrefix}-prazo`} className="mb-0" style={{ fontSize: '12px' }}>
            Prazo em dias
          </Label>
        </FormGroup>
        <FormGroup check inline className="mb-0">
          <Input
            type="radio"
            id={`${idPrefix}-data`}
            name={`${idPrefix}-agendamento`}
            checked={mode === ENTREGAVEL_AGENDAMENTO_DATA_FIXA}
            disabled={disabled}
            onChange={() => onChange(ENTREGAVEL_AGENDAMENTO_DATA_FIXA)}
          />
          <Label check for={`${idPrefix}-data`} className="mb-0" style={{ fontSize: '12px' }}>
            Data prevista
          </Label>
        </FormGroup>
      </div>
    </FormGroup>
  );
}

function EntregavelRow({
  index,
  descricao,
  modoAgendamento,
  prazoDias,
  dtPrev,
  valorPct,
  valorVlr = '',
  disabled,
  onDescricaoChange,
  onModoAgendamentoChange,
  onPrazoDiasChange,
  onDtPrevChange,
  onValorPctChange,
  onValorVlrChange,
  onRemove,
  showRemove,
}) {
  const isDataFixa = modoAgendamento === ENTREGAVEL_AGENDAMENTO_DATA_FIXA;

  return (
    <Row className="align-items-end mb-2 mx-0 w-100">
      <Col xs="auto" className="px-1 pb-2">
        {showRemove ? (
          <IconButton
            size="small"
            disabled={disabled}
            onClick={onRemove}
            aria-label="Remover entregável"
          >
            <Delete fontSize="small" />
          </IconButton>
        ) : (
          <span style={{ display: 'inline-block', width: 40 }} />
        )}
      </Col>
      <Col md={3} sm={12} className="px-1">
        <FormGroup className="mb-2">
          <Label className="text-muted" style={fieldLabelStyle}>Descrição</Label>
          <Input
            type="text"
            value={descricao}
            disabled={disabled}
            onChange={(e) => onDescricaoChange(e.target.value)}
          />
        </FormGroup>
      </Col>
      <Col md={2} sm={6} className="px-1">
        <AgendamentoModeToggle
          idPrefix={`entregavel-${index}`}
          mode={modoAgendamento}
          disabled={disabled}
          onChange={onModoAgendamentoChange}
        />
      </Col>
      <Col md={2} sm={6} className="px-1">
        {isDataFixa ? (
          <FormGroup className="mb-2">
            <DataPrevistaLabel />
            <DatePicker
              placeholderText="__/__/_____"
              locale="pt-BR"
              className="form-control"
              dateFormat="dd/MM/yyyy"
              dropdownMode="select"
              disabled={disabled}
              selected={dtPrev ? moment(dtPrev).toDate() : null}
              onChange={(date) => {
                onDtPrevChange(date ? moment(date).format('YYYY-MM-DD') : null);
              }}
            />
          </FormGroup>
        ) : (
          <FormGroup className="mb-2">
            <PrazoEntregaLabel />
            <Input
              type="number"
              min={0}
              step={1}
              value={prazoDias}
              disabled={disabled}
              onWheel={preventWheelChange}
              onChange={(e) => onPrazoDiasChange(e.target.value)}
            />
          </FormGroup>
        )}
      </Col>
      <Col md={2} sm={4} className="px-1">
        <FormGroup className="mb-2">
          <Label className="text-muted" style={fieldLabelStyle}>Valor %</Label>
          <Input
            type="number"
            min={0}
            max={100}
            step={0.01}
            value={valorPct}
            disabled={disabled}
            onWheel={preventWheelChange}
            onChange={(e) => onValorPctChange(e.target.value)}
          />
        </FormGroup>
      </Col>
      <Col md={2} sm={4} className="px-1">
        <FormGroup className="mb-2">
          <Label className="text-muted" style={fieldLabelStyle}>Valor (R$)</Label>
          <Input
            type="number"
            min={0}
            step={0.01}
            value={valorVlr}
            disabled={disabled}
            onWheel={preventWheelChange}
            onChange={(e) => onValorVlrChange(e.target.value)}
          />
        </FormGroup>
      </Col>
    </Row>
  );
}

function ContrFornValidationEntregaveis({
  valorGlobalProposta = 0,
  entregaveis = [],
  onChange = () => {},
  onDraftChange = () => {},
  disabled = false,
  parcelasMensaisFixas = false,
  onParcelasMensaisFixasChange = () => {},
}) {
  const [draft, setDraft] = React.useState(EMPTY_DRAFT);
  const [qtdLote, setQtdLote] = React.useState('');
  const [descricaoPadraoLote, setDescricaoPadraoLote] = React.useState('');
  const valorGlobal = Number(valorGlobalProposta || 0);
  const totalValorCommitido = getSomaValorEntregaveis(entregaveis);

  const parcelaDraft = React.useMemo(() => {
    if (!isEntregavelDraftComValor(draft, valorGlobal)) return null;
    const parcela = buildEntregavelParcelaFromDraft(draft, entregaveis.length, valorGlobal);
    const persistivel = !validarEntregavelDraft(draft, {
      somaValorAtual: totalValorCommitido,
      valorGlobal,
    });
    return {
      ...parcela,
      _isDraft: true,
      _draftPersistivel: persistivel,
    };
  }, [draft, entregaveis.length, valorGlobal, totalValorCommitido]);

  const entregaveisEfetivos = parcelaDraft ? [...entregaveis, parcelaDraft] : entregaveis;
  const somaPercentual = getSomaPercentualEntregaveis(entregaveisEfetivos);
  const totalValor = getSomaValorEntregaveis(entregaveisEfetivos);
  const totalBateReferencia = entregaveisTotalBateComValorGlobal(entregaveisEfetivos, valorGlobal);

  React.useEffect(() => {
    onDraftChange(parcelaDraft);
  }, [parcelaDraft, onDraftChange]);

  const updateEntregavel = (index, patch, agendamentoMode, { redistributeCents } = {}) => {
    const next = entregaveis.map((item, itemIndex) => {
      if (itemIndex !== index) return item;

      const merged = {
        ...item,
        ...patch,
      };

      return buildEntregavelParcela({
        descricao: merged.contrFornEvento_desc,
        prazoDias: merged.contrFornEvento_pgtoPrazoDias,
        dtPrev: merged.contrFornEvento_dtPrev,
        valorPct: merged.valorPct,
        valorVlr: merged.contrFornEvento_vlr,
        valueSource: merged._valueSource,
        agendamentoMode: agendamentoMode || getEntregavelAgendamentoMode(merged),
      }, index, valorGlobal, merged);
    });

    const total = getSomaValorEntregaveis(next);
    if (valorGlobal > 0 && total > Number(Number(valorGlobal).toFixed(2))) {
      notifyWarning('A soma dos valores não pode ultrapassar o valor da proposta.');
      return;
    }

    onChange(renumberEntregaveis(next, valorGlobal, { redistributeCents }));
  };

  const handleModoAgendamentoChange = (index, mode) => {
    const item = entregaveis[index];
    if (!item) return;
    const switched = applyEntregavelAgendamentoMode(item, mode);
    updateEntregavel(index, switched, mode);
  };

  const handleAddEntregavel = () => {
    if (disabled) return;

    if (!valorGlobal) {
      notifyWarning('Não foi possível identificar o valor global da proposta.');
      return;
    }

    const validationError = validarEntregavelDraft(draft, {
      somaValorAtual: totalValorCommitido,
      valorGlobal,
    });
    if (validationError) {
      notifyWarning(validationError);
      return;
    }

    const nextEntregaveis = renumberEntregaveis([
      ...entregaveis,
      buildEntregavelParcelaFromDraft(draft, entregaveis.length, valorGlobal),
    ], valorGlobal, { redistributeCents: draft.valueSource !== 'vlr' });

    onChange(nextEntregaveis);
    setDraft(EMPTY_DRAFT);
  };

  const handleRemoveEntregavel = (index) => {
    if (disabled) return;
    const nextEntregaveis = entregaveis.filter((_, itemIndex) => itemIndex !== index);
    onChange(renumberEntregaveis(nextEntregaveis, valorGlobal));
  };

  const handleAddEntregaveisEmLote = () => {
    if (disabled) return;

    if (!valorGlobal) {
      notifyWarning('Não foi possível identificar o valor global da proposta.');
      return;
    }

    const quantidade = Number.parseInt(String(qtdLote), 10);
    if (!Number.isFinite(quantidade) || quantidade < 1) {
      notifyWarning('Informe uma quantidade inteira maior que zero.');
      return;
    }

    const restante = Number((valorGlobal - totalValor).toFixed(2));
    if (!(restante > 0)) {
      notifyWarning('Não há valor restante da proposta para dividir em novos entregáveis.');
      return;
    }

    const lote = buildEntregaveisEmLote({
      quantidade,
      descricaoPadrao: descricaoPadraoLote,
      restante,
      valorGlobal,
      startIndex: entregaveis.length,
    });

    if (!lote.length) {
      notifyWarning('Não foi possível gerar os entregáveis em lote.');
      return;
    }

    onChange(renumberEntregaveis([...entregaveis, ...lote], valorGlobal, {
      redistributeCents: false,
    }));

    if (isEntregavelDraftVazio(draft)) {
      setDraft(EMPTY_DRAFT);
    }
    setQtdLote('');
  };

  const draftIsDataFixa = draft.agendamentoMode === ENTREGAVEL_AGENDAMENTO_DATA_FIXA;

  return (
    <div className="mb-4 w-100">
      <div style={sectionDividerStyle} aria-hidden="true" />
      <h5 className="mb-3" style={{ fontWeight: 600 }}>Entregáveis</h5>

      <div
        className="d-flex flex-wrap align-items-end mb-3"
        style={{ gap: '12px' }}
      >
        <FormGroup check className="mb-0" style={{ paddingBottom: '8px' }}>
          <Input
            type="checkbox"
            id="parcelas-mensais-fixas"
            checked={Boolean(parcelasMensaisFixas)}
            disabled={disabled}
            onChange={(e) => onParcelasMensaisFixasChange(e.target.checked)}
          />
          <Label check for="parcelas-mensais-fixas" className="mb-0">
            As parcelas terão valores mensais fixos
          </Label>
        </FormGroup>
        <FormGroup className="mb-0" style={{ minWidth: 110, maxWidth: 140 }}>
          <Label className="text-muted" style={fieldLabelStyle}>Quantidade</Label>
          <Input
            type="number"
            min={1}
            step={1}
            placeholder="Qtd"
            value={qtdLote}
            disabled={disabled || !valorGlobal}
            onWheel={preventWheelChange}
            onChange={(e) => setQtdLote(e.target.value)}
          />
        </FormGroup>
        <FormGroup className="mb-0" style={{ flex: 1, minWidth: 160 }}>
          <Label className="text-muted" style={fieldLabelStyle}>Descrição</Label>
          <Input
            type="text"
            placeholder="Entregável (opcional)"
            value={descricaoPadraoLote}
            disabled={disabled || !valorGlobal}
            onChange={(e) => setDescricaoPadraoLote(e.target.value)}
          />
        </FormGroup>
        <Button
          type="button"
          color="primary"
          className="mb-0"
          disabled={disabled || !valorGlobal}
          onClick={handleAddEntregaveisEmLote}
        >
          Adicionar em lote
        </Button>
      </div>

      {!valorGlobal && (
        <div className="alert alert-danger mb-3" role="alert">
          O valor global da proposta não foi encontrado em <code>compraCotacao_info</code>.
        </div>
      )}

      {entregaveis.map((entregavel, index) => {
        const modoAgendamento = getEntregavelAgendamentoMode(entregavel);
        return (
          <EntregavelRow
            key={entregavel.contrFornEvento_id || `entregavel-${index}`}
            index={index}
            descricao={entregavel.contrFornEvento_desc || ''}
            modoAgendamento={modoAgendamento}
            prazoDias={entregavel.contrFornEvento_pgtoPrazoDias ?? ''}
            dtPrev={entregavel.contrFornEvento_dtPrev ?? null}
            valorPct={getValorPctFromEntregavel(entregavel) ?? ''}
            valorVlr={entregavel.contrFornEvento_vlr ?? ''}
            disabled={disabled}
            showRemove
            onDescricaoChange={(value) => updateEntregavel(index, {
              contrFornEvento_desc: value,
            }, modoAgendamento)}
            onModoAgendamentoChange={(mode) => handleModoAgendamentoChange(index, mode)}
            onPrazoDiasChange={(value) => updateEntregavel(index, {
              contrFornEvento_pgtoPrazoDias: value,
            }, ENTREGAVEL_AGENDAMENTO_PRAZO)}
            onDtPrevChange={(value) => updateEntregavel(index, {
              contrFornEvento_dtPrev: value,
            }, ENTREGAVEL_AGENDAMENTO_DATA_FIXA)}
            onValorPctChange={(value) => updateEntregavel(index, {
              valorPct: value,
              _valueSource: 'pct',
            }, modoAgendamento, { redistributeCents: true })}
            onValorVlrChange={(value) => updateEntregavel(index, {
              contrFornEvento_vlr: value,
              _valueSource: 'vlr',
            }, modoAgendamento, { redistributeCents: false })}
            onRemove={() => handleRemoveEntregavel(index)}
          />
        );
      })}

      <Row className="align-items-end mb-0 mx-0 w-100">
        <Col xs="auto" className="px-1 pb-2">
          <IconButton
            size="small"
            disabled={disabled || !valorGlobal}
            onClick={handleAddEntregavel}
            aria-label="Adicionar entregável"
            sx={{
              backgroundColor: '#8bc34a',
              color: '#1b1b1b',
              '&:hover': { backgroundColor: '#7cb342' },
              '&.Mui-disabled': { backgroundColor: '#e0e0e0', color: '#9e9e9e' },
            }}
          >
            <Add />
          </IconButton>
        </Col>
        <Col md={3} sm={12} className="px-1">
          <FormGroup className="mb-2">
            <Label className="text-muted" style={fieldLabelStyle}>Descrição</Label>
            <Input
              type="text"
              value={draft.descricao}
              disabled={disabled || !valorGlobal}
              onChange={(e) => setDraft((prev) => ({ ...prev, descricao: e.target.value }))}
            />
          </FormGroup>
        </Col>
        <Col md={2} sm={6} className="px-1">
          <AgendamentoModeToggle
            idPrefix="entregavel-draft"
            mode={draft.agendamentoMode || ENTREGAVEL_AGENDAMENTO_PRAZO}
            disabled={disabled || !valorGlobal}
            onChange={(mode) => setDraft((prev) => {
              if (mode === ENTREGAVEL_AGENDAMENTO_DATA_FIXA) {
                return {
                  ...prev,
                  agendamentoMode: mode,
                  dtPrev: prev.dtPrev || prev.dtPrevRemembered || null,
                  prazoRemembered: (prev.prazoDias !== '' && prev.prazoDias != null)
                    ? prev.prazoDias
                    : (prev.prazoRemembered ?? null),
                  prazoDias: '',
                };
              }
              return {
                ...prev,
                agendamentoMode: mode,
                dtPrevRemembered: prev.dtPrev || prev.dtPrevRemembered || null,
                dtPrev: null,
                prazoDias: (prev.prazoDias !== '' && prev.prazoDias != null)
                  ? prev.prazoDias
                  : (prev.prazoRemembered ?? ''),
              };
            })}
          />
        </Col>
        <Col md={2} sm={6} className="px-1">
          {draftIsDataFixa ? (
            <FormGroup className="mb-2">
              <DataPrevistaLabel />
              <DatePicker
                placeholderText="__/__/_____"
                locale="pt-BR"
                className="form-control"
                dateFormat="dd/MM/yyyy"
                dropdownMode="select"
                disabled={disabled || !valorGlobal}
                selected={draft.dtPrev ? moment(draft.dtPrev).toDate() : null}
                onChange={(date) => setDraft((prev) => {
                  const nextDtPrev = date ? moment(date).format('YYYY-MM-DD') : null;
                  return {
                    ...prev,
                    dtPrev: nextDtPrev,
                    dtPrevRemembered: nextDtPrev || prev.dtPrevRemembered || null,
                  };
                })}
              />
            </FormGroup>
          ) : (
            <FormGroup className="mb-2">
              <PrazoEntregaLabel />
              <Input
                type="number"
                min={0}
                step={1}
                value={draft.prazoDias}
                disabled={disabled || !valorGlobal}
                onWheel={preventWheelChange}
                onChange={(e) => setDraft((prev) => ({
                  ...prev,
                  prazoDias: e.target.value,
                  prazoRemembered: (e.target.value !== '' && e.target.value != null)
                    ? e.target.value
                    : (prev.prazoRemembered ?? null),
                }))}
              />
            </FormGroup>
          )}
        </Col>
        <Col md={2} sm={4} className="px-1">
          <FormGroup className="mb-2">
            <Label className="text-muted" style={fieldLabelStyle}>Valor %</Label>
            <Input
              type="number"
              min={0}
              max={100}
              step={0.01}
              value={draft.valorPct}
              disabled={disabled || !valorGlobal}
              onWheel={preventWheelChange}
              onChange={(e) => {
                const valorPct = e.target.value;
                setDraft((prev) => ({
                  ...prev,
                  valorPct,
                  valueSource: 'pct',
                  valorVlr: valorPct === ''
                    ? ''
                    : calcularValorEntregavel(valorGlobal, valorPct) || '',
                }));
              }}
            />
          </FormGroup>
        </Col>
        <Col md={2} sm={4} className="px-1">
          <FormGroup className="mb-2">
            <Label className="text-muted" style={fieldLabelStyle}>Valor (R$)</Label>
            <Input
              type="number"
              min={0}
              step={0.01}
              value={draft.valorVlr}
              disabled={disabled || !valorGlobal}
              onWheel={preventWheelChange}
              onChange={(e) => {
                const valorVlr = e.target.value;
                setDraft((prev) => ({
                  ...prev,
                  valorVlr,
                  valueSource: 'vlr',
                  valorPct: valorVlr === ''
                    ? ''
                    : calcularPctEntregavel(valorGlobal, valorVlr) || '',
                }));
              }}
            />
          </FormGroup>
        </Col>
      </Row>

      <div
        className="pt-3 mt-2 d-flex flex-wrap"
        style={{
          gap: '1rem',
          borderTop: '1px solid #e6e6e6',
        }}
      >
        <span>
          <Label className="text-muted d-block mb-0" style={fieldLabelStyle}>Soma</Label>
          <strong>{somaPercentual.toFixed(2)}%</strong>
        </span>
        <span>
          <Label className="text-muted d-block mb-0" style={fieldLabelStyle}>Total</Label>
          <strong className={totalBateReferencia ? 'text-success' : 'text-danger'}>
            {currencyFormatter.format(totalValor)}
          </strong>
          {' '}
          / referência:
          {' '}
          <strong>{currencyFormatter.format(valorGlobal)}</strong>
        </span>
        {somaPercentual !== 100 && totalBateReferencia && (
          <span className="text-muted align-self-end">
            Soma percentual: {somaPercentual.toFixed(2)}% (indicativo).
          </span>
        )}
        {!totalBateReferencia && (
          <span className="text-danger align-self-end">
            O total dos entregáveis deve ser igual ao valor da proposta.
          </span>
        )}
      </div>
    </div>
  );
}

export default ContrFornValidationEntregaveis;
